import React from 'react'
import { action, computed, makeObservable, observable, toJS } from 'mobx'

const defaultControls = {
    volume: true,
    seeking: true,
    seekingforward: true,
    seekPlus10: true,
    seekMinus10: true
}

export const VIDEO_PLAYER_STATUS = {
    ERROR: 'error',
    LOADING: 'loading',
    READY: 'ready',
    ENDED: 'ended',
    BUFFERING: 'buffering',
    PLAYING: 'playing',
    PAUSED: 'paused'
}

const defaultEditOptions = {
    canScrubTimelineEvents: false,
    canDeleteTimelineEvents: false,
    canAddTimelineEvents: false
}

const normalizeEvents = ( events ) => {
    if ( !Array.isArray( events ) ) return []
    // copy first: sorting in place mutates the parent's prop array (and MobX warns on observable arrays)
    return [...events]
        .sort( ( e1, e2 ) => ( e1.time ?? e1.triggerTime ?? 0 ) - ( e2.time ?? e2.triggerTime ?? 0 ) )
        .map( ( event, index ) => ( {
            id: event.id ?? index,
            eventIndex: index,
            data: event.data || {},
            time: typeof event.time === 'number' ? event.time : typeof event.triggerTime === 'number' ? event.triggerTime : 0,
            label: event.label ?? event.title ?? `Event ${index + 1}`,
            displayLabel: event.displayLabel
        } ) )
}

// Timeline events are scheduled as cues on a hidden "metadata" TextTrack so the browser
// fires them when playback reaches their time, instead of us scanning every event on
// each timeupdate.
const EVENT_TRACK_LABEL = 'timeline-events'
// Cues stay active from the event time until the end of the media. This means:
//  - a forward seek past an event makes its cue active, so it still fires (as before),
//  - seeking back before an event deactivates the cue, so it fires again on replay,
//  - seeking around after an event does not re-fire it.
const EVENT_CUE_END_TIME = Number.MAX_SAFE_INTEGER
// One track per <video> element. addTextTrack() tracks can't be removed, so reuse them
// (e.g. when React re-attaches the same element in StrictMode).
const eventTracks = new WeakMap()

const getEventTrack = ( video ) => {
    if ( !video || typeof video.addTextTrack !== 'function' ) return null
    let track = eventTracks.get( video )
    if ( !track ) {
        track = video.addTextTrack( 'metadata', EVENT_TRACK_LABEL )
        eventTracks.set( video, track )
    }
    // 'hidden' = cues are processed and fire enter/exit, but nothing is rendered
    track.mode = 'hidden'
    return track
}

const removeCueSafely = ( track, cue ) => {
    cue.onenter = null
    cue.onexit = null
    try {
        track.removeCue( cue )
    } catch ( err ) { }
}

const getSafePosition = ( time, duration ) => {
    if ( !duration || duration <= 0 ) return 0
    return Math.min( 100, Math.max( 0, ( time / duration ) * 100 ) )
}

export class VideoPlayerStore {
    // HTML Element refs
    videoPlayerRef = null
    timelineContainerRef = null
    controlsContainerRef = null
    videoContainerRef = null
    fullscreenElement = null

    // States
    attachmentDataLoading = false
    isInFullscreenMode = false
    videoPlayerStatus = VIDEO_PLAYER_STATUS.LOADING
    isEditModeActive = false
    showReplayBtn = false
    isBuffering = true
    isSeeking = false
    wasPaused = false
    isPaused = true
    controls = true
    error = null

    // Values
    overlayDisappearTimeoutId = null
    eventTrack = null
    eventCues = new Map()
    draggingEventId = null
    draggingTime = null
    editableEvents = []
    maxTimeWatched = 0
    videoProgress = 0
    currentTime = 0
    duration = 0
    lastTime = 0
    volume = 1

    config = {
        defaults: {
            start: 0,
            volume: 1
        },
        controllerOptions: defaultControls,
        editOptions: defaultEditOptions,
        events: []
    }

    onEventsChangeCallback = null
    onEventCallback = null

    constructor ( { config = {}, onEvent, onEventsChange, controls = true } = {} ) {
        this.setConfig( config )
        this.setControls( controls )
        this.updateEditableEvents( this.config.events )
        if ( typeof onEvent === 'function' ) this.onEventCallback = onEvent
        if ( typeof onEventsChange === 'function' ) this.onEventsChangeCallback = onEventsChange

        makeObservable( this, {
            controlsContainerRef: observable.ref,
            attachmentDataLoading: observable,
            isEditModeActive: observable,
            isInFullscreenMode: observable,
            showReplayBtn: observable,
            isBuffering: observable,
            isPaused: observable,
            volume: observable,
            duration: observable,
            videoProgress: observable,
            currentTime: observable,
            error: observable,
            maxTimeWatched: observable,
            isSeeking: observable,
            controls: observable,
            wasPaused: observable,
            draggingEventId: observable,
            videoPlayerStatus: observable,
            draggingTime: observable,
            editableEvents: observable,
            lastTime: observable,
            config: observable,
            editModePossible: computed,
            controllerOptions: computed,
            videoDuration: computed,
            setConfig: action,
            setControls: action,
            setCallbacks: action,
            setAttachmentDataLoading: action,
            setError: action,
            setVolume: action,
            setIsBuffering: action,
            setIsPaused: action,
            setShowReplayBtn: action,
            setIsEditModeActive: action,
            setMaxTimeWatched: action,
            setWasPaused: action,
            setDraggingEventId: action,
            setDraggingTime: action,
            setFullscreenElement: action,
            setEvents: action,
            updateEditableEvents: action,
            updateEventList: action,
            calculateDuration: action,
            handleVolumeSlide: action,
            toggleMute: action,
            togglePlay: action,
            handleOnCanPlay: action,
            handleMouseEnter: action,
            handleMouseLeave: action,
            handleLoadedMetadata: action,
            handlePause: action,
            handlePlay: action,
            handleVolumeChange: action,
            handleVideoBuffer: action,
            handleVideoData: action,
            handleMouseOnTimeline: action,
            toggleSeeking: action,
            endSeeking: action,
            seekPlusTen: action,
            seekMinusTen: action,
            toggleFullScreen: action,
            handleFullScreenChange: action,
            handleKeyDown: action,
            loadDefaults: action,
            handleAddEvent: action,
            handleDeleteEvent: action,
            handleClickOnEventChip: action,
            handleEventMarkerPointerDown: action,
            handleEventDrag: action,
            handleEventDragStop: action,
            updateProgress: action,
            setError: action
        } )
    }

    get videoDuration () {
        return this.videoPlayerRef?.duration || 0
    }

    get editModePossible () {
        return Array.isArray( this.config.events ) && Object.values( this.config.editOptions || {} ).some( Boolean )
    }

    get controllerOptions () {
        return this.config.controllerOptions || defaultControls
    }

    setVideoPlayerRef = ( ref ) => {
        if ( ref === this.videoPlayerRef ) return
        this.detachEventTrack()
        this.videoPlayerRef = ref
        if ( ref ) this.attachEventTrack( ref )
    }

    attachEventTrack = ( video ) => {
        this.eventCues = new Map()
        this.eventTrack = getEventTrack( video )
        if ( !this.eventTrack ) return
        // clear anything left over from a previous attach of the same element
        Array.from( this.eventTrack.cues || [] ).forEach( ( cue ) => removeCueSafely( this.eventTrack, cue ) )
        this.syncEventCues()
    }

    detachEventTrack = () => {
        const track = this.eventTrack
        if ( track ) {
            this.eventCues.forEach( ( cue ) => removeCueSafely( track, cue ) )
            track.mode = 'disabled'
        }
        this.eventCues = new Map()
        this.eventTrack = null
    }

    // Call when the player unmounts
    dispose = () => {
        this.detachEventTrack()
    }

    // Keeps the metadata track's cues in sync with editableEvents. Cues whose time hasn't
    // changed are reused so they keep their active/inactive state.
    syncEventCues = () => {
        const track = this.eventTrack
        const video = this.videoPlayerRef
        const Cue = typeof window !== 'undefined' ? window.VTTCue : undefined
        if ( !track || !video || typeof Cue !== 'function' ) return

        const duration = Number.isFinite( video.duration ) && video.duration > 0 ? video.duration : null
        const currentTime = video.currentTime || 0
        const nextCues = new Map()

        this.editableEvents.forEach( ( event ) => {
            if ( nextCues.has( event.id ) ) return
            const clampedStart = Math.max( 0, event.time || 0 )
            const startTime = duration === null ? clampedStart : Math.min( clampedStart, duration )
            let cue = this.eventCues.get( event.id )

            if ( cue && cue.startTime === startTime ) {
                // unchanged, keep it (and its active state)
                this.eventCues.delete( event.id )
            } else {
                cue = new Cue( startTime, EVENT_CUE_END_TIME, String( event.label ?? '' ) )
                cue.id = String( event.id )
                // A cue added at/behind the playhead becomes active immediately. That is the
                // result of editing the list (add / drag / config change), not playback
                // reaching the event, so don't fire it. The old per-tick check had the same
                // behaviour because it required lastTime < eventTime.
                cue.skipNextEnter = startTime <= currentTime
                cue.onenter = this.handleEventCueEnter
                cue.onexit = this.handleEventCueExit
                track.addCue( cue )
            }
            cue.timelineEvent = { ...event }
            nextCues.set( event.id, cue )
        } )

        // whatever is left belongs to deleted or moved events
        this.eventCues.forEach( ( cue ) => removeCueSafely( track, cue ) )
        this.eventCues = nextCues
    }

    handleEventCueEnter = ( cueEvent ) => {
        const cue = cueEvent.target
        if ( cue.skipNextEnter ) {
            cue.skipNextEnter = false
            return
        }
        if ( typeof this.onEventCallback !== 'function' ) return
        const event = cue.timelineEvent
        const video = this.videoPlayerRef
        try {
            this.onEventCallback( {
                event: { ...event, id: event.id, time: event.time, label: event.label },
                currentTime: video?.currentTime ?? cue.startTime,
                video,
                markerEle: document.getElementById( `timeline-event-marker-${event.id}` )
            } )
        } catch ( err ) {
            console.error( 'Timeline event handler failed', err )
        }
    }

    handleEventCueExit = ( cueEvent ) => {
        // playhead moved back before the event; next time it's reached it should fire
        cueEvent.target.skipNextEnter = false
    }

    setTimelineContainerRef = ( ref ) => {
        this.timelineContainerRef = ref
    }

    setControlsContainerRef = ( ref ) => {
        this.controlsContainerRef = ref
    }

    setVideoContainerRef = ( ref ) => {
        this.videoContainerRef = ref
    }

    setConfig = ( config = {} ) => {
        this.config = {
            defaults: {
                ...this.config.defaults,
                ...( config.defaults || {} )
            },
            controllerOptions: {
                ...this.config.controllerOptions,
                ...( config.controllerOptions || {} )
            },
            editOptions: {
                ...this.config.editOptions,
                ...( config.editOptions || {} )
            },
            events: Array.isArray( config.events ) ? config.events : this.config.events
        }
        this.updateEditableEvents( this.config.events )
    }

    setControls = ( value ) => {
        this.controls = value
    }

    setCallbacks = ( { onEvent, onEventsChange } ) => {
        if ( typeof onEvent === 'function' ) this.onEventCallback = onEvent
        if ( typeof onEventsChange === 'function' ) this.onEventsChangeCallback = onEventsChange
    }

    setAttachmentDataLoading = ( value ) => {
        this.attachmentDataLoading = value
    }

    setError = ( value ) => {
        this.error = value
        if ( value ) this.videoPlayerStatus = VIDEO_PLAYER_STATUS.ERROR
    }

    setVolume = ( value ) => {
        this.volume = Number( value )
        if ( this.videoPlayerRef ) {
            this.videoPlayerRef.volume = this.volume
            this.videoPlayerRef.muted = this.volume === 0
        }
    }

    setIsBuffering = ( value ) => {
        this.isBuffering = value
        if ( value ) {
            this.videoPlayerStatus = VIDEO_PLAYER_STATUS.BUFFERING
        } else {
            this.videoPlayerStatus = this.isPaused ? VIDEO_PLAYER_STATUS.PAUSED : VIDEO_PLAYER_STATUS.PLAYING
        }
    }

    setIsPaused = ( value ) => {
        this.isPaused = value
    }

    setShowReplayBtn = ( value ) => {
        this.showReplayBtn = value
        if ( value ) this.videoPlayerStatus = VIDEO_PLAYER_STATUS.ENDED
    }

    setIsEditModeActive = ( value ) => {
        this.isEditModeActive = value
    }

    setMaxTimeWatched = ( value ) => {
        this.maxTimeWatched = value
    }

    setWasPaused = ( value ) => {
        this.wasPaused = value
    }

    setDraggingEventId = ( id ) => {
        this.draggingEventId = id
    }

    setDraggingTime = ( time ) => {
        this.draggingTime = time
    }

    setFullscreenElement = ( element ) => {
        this.fullscreenElement = element
    }

    setEvents = ( events ) => {
        this.config.events = Array.isArray( events ) ? events : []
        this.updateEditableEvents( this.config.events )
    }

    updateEditableEvents = ( events ) => {
        this.editableEvents = normalizeEvents( events )
        this.syncEventCues()
    }

    updateEventList = ( nextEvents ) => {
        const normalized = normalizeEvents( nextEvents )
        this.editableEvents = normalized
        this.syncEventCues()
        // keep the canonical config.events in sync with editableEvents
        try {
            this.config.events = normalized.map( ( e ) => ( { ...e } ) )
        } catch ( err ) { }
        if ( typeof this.onEventsChangeCallback === 'function' ) {
            this.onEventsChangeCallback( toJS( normalized.map( ( event ) => ( { ...event } ) ) ) )
        }
    }

    calculateDuration () {
        if ( this.videoPlayerRef ) {
            this.duration = this.videoPlayerRef.duration || 0
        }
    }

    handleLoadedMetadata = () => {
        this.loadDefaults()
        this.calculateDuration()
        this.syncEventCues()
        this.handleVideoData()
        this.handleVideoBuffer()
        this.setAttachmentDataLoading( false )
    }

    handleAddEvent = () => {
        const currentTime = this.videoPlayerRef?.currentTime || 0
        const newEvent = {
            id: `event-${Date.now()}`,
            time: Math.min( currentTime, this.duration ),
            label: `Event ${this.editableEvents.length + 1}`
        }
        this.updateEventList( [...this.editableEvents, newEvent].sort( ( e1, e2 ) => e1.time - e2.time ) )
    }

    handleDeleteEvent = ( id ) => {
        this.updateEventList( this.editableEvents.filter( ( event ) => event.id !== id ) )
    }

    handleClickOnEventChip = ( event ) => {
        const marker = document.getElementById( `timeline-event-marker-${event.id}` )
        if ( marker ) {
            marker.classList.add( 'highlighted-video-timeline-event-marker' )
            setTimeout( () => {
                marker.classList.remove( 'highlighted-video-timeline-event-marker' )
            }, 2000 )
        }
    }

    toggleMute = () => {
        if ( !this.videoPlayerRef ) return
        const player = this.videoPlayerRef
        player.muted = !player.muted
        this.volume = player.muted ? 0 : player.volume
    }

    handleVolumeSlide = ( event, newValue ) => {
        const nextValue = typeof newValue === 'number' ? newValue : event.target.value
        this.setVolume( nextValue )
    }

    togglePlay = async () => {
        if ( !this.videoPlayerRef ) return
        try {
            const player = this.videoPlayerRef
            player.paused ? await player.play() : await player.pause()
        } catch ( err ) { }
    }

    handleOnCanPlay = async ( event ) => {
        this.setIsBuffering( false )
        this.videoPlayerStatus = VIDEO_PLAYER_STATUS.READY
        try {
            if ( this.videoPlayerRef?.paused && event.target.autoplay && !this.wasPaused ) {
                await this.videoPlayerRef.play()
            }
        } catch ( err ) { }
    }

    handlePlay = () => {
        this.setIsPaused( false )
        this.controlsContainerRef?.classList.remove( 'paused' )
        this.setShowReplayBtn( false )
        this.videoPlayerStatus = VIDEO_PLAYER_STATUS.PLAYING
    }

    handleTimelineMouseDown = async ( event ) => {
        if ( this.config.controllerOptions.seeking === false ) return
        const { wasPaused, isSeeking } = await this.toggleSeeking( event )
        this.isSeeking = isSeeking
        this.setWasPaused( wasPaused )
    }

    handleMouseEnter = ( event ) => {
        if ( this.overlayDisappearTimeoutId ) {
            clearTimeout( this.overlayDisappearTimeoutId )
            this.overlayDisappearTimeoutId = null
        }
        if ( this.controlsContainerRef ) {
            this.controlsContainerRef.classList.add( 'hovered' )
            if ( event.type !== 'mouseenter' ) {
                this.overlayDisappearTimeoutId = window.setTimeout( () => {
                    this.controlsContainerRef.classList.remove( 'hovered' )
                    this.overlayDisappearTimeoutId = null
                }, 5000 )
            }
        }
    }

    handleMouseLeave = ( controls ) => {
        if ( controls ) {
            window.setTimeout( () => {
                controls.classList.remove( 'hovered' )
            }, 500 )
        }
    }

    handlePause = () => {
        this.setIsPaused( true )
        this.controlsContainerRef?.classList.add( 'paused' )
        this.videoPlayerStatus = VIDEO_PLAYER_STATUS.PAUSED
    }

    handleVolumeChange = () => {
        if ( !this.videoPlayerRef ) return
        this.volume = this.videoPlayerRef.muted ? 0 : this.videoPlayerRef.volume
    }

    handleVideoBuffer = () => {
        if ( !this.videoPlayerRef || !this.timelineContainerRef ) return
        try {
            const video = this.videoPlayerRef
            const bf = video.buffered
            const time = video.currentTime
            let range = 0
            while ( !( bf.start( range ) <= time && time <= bf.end( range ) ) ) {
                range += 1
                if ( range >= bf.length ) break
            }
            if ( range < bf.length ) {
                const loadStartPercentage = bf.start( range ) / video.duration
                const loadEndPercentage = bf.end( range ) / video.duration
                const loadPercentage = loadEndPercentage - loadStartPercentage
                this.timelineContainerRef.style.setProperty( '--buffer-position', loadPercentage )
            }
        } catch ( err ) { }
    }

    handleVideoData = () => {
        if ( !this.videoPlayerRef || !this.timelineContainerRef ) return
        const percent = this.videoPlayerRef.currentTime / this.videoPlayerRef.duration
        this.videoProgress = percent
        this.timelineContainerRef.style.setProperty( '--progress-position', percent )
    }

    handleMouseOnTimeline = ( event, isSeeking, scrubVideoOnMove = false ) => {
        const boundary = this.timelineContainerRef?.getBoundingClientRect()
        if ( !boundary || !this.timelineContainerRef ) return
        const clientX = event.clientX !== undefined
            ? event.clientX
            : ( event.touches?.[0]?.clientX || event.changedTouches?.[0]?.clientX || 0 )
        const percent = Math.min( Math.max( 0, clientX - boundary.x ), boundary.width ) / boundary.width
        this.timelineContainerRef.style.setProperty( '--preview-position', percent )
        if ( isSeeking ) {
            event.preventDefault()
            this.timelineContainerRef.style.setProperty( '--progress-position', percent )
            this.controlsContainerRef?.classList.add( 'scrubbing' )
            if ( scrubVideoOnMove && this.videoPlayerRef ) {
                this.videoPlayerRef.currentTime = percent * this.videoPlayerRef.duration
            }
        }
    }

    toggleSeeking = async ( event ) => {
        if ( !this.timelineContainerRef || !this.videoPlayerRef ) return { isSeeking: false, wasPaused: this.wasPaused }
        const boundary = this.timelineContainerRef.getBoundingClientRect()
        const clientX = event.clientX !== undefined
            ? event.clientX
            : ( event.touches?.[0]?.clientX || event.changedTouches?.[0]?.clientX || 0 )
        const percent = Math.min( Math.max( 0, clientX - boundary.x ), boundary.width ) / boundary.width
        const isSeekingNow = ( event.buttons & 1 ) === 1
        const targetTime = percent * this.videoPlayerRef.duration
        const isTryingToSkipAhead = targetTime > this.maxTimeWatched
        const isForwardRestricted = this.config.controllerOptions.seekingforward === false

        this.timelineContainerRef.classList.toggle( 'scrubbing', isSeekingNow )
        let paused = isSeekingNow ? this.videoPlayerRef.paused : this.wasPaused

        // Set these synchronously (before the awaits below) so that a pointermove firing
        // while play()/pause() is still pending (e.g. the video is buffering after a seek)
        // sees the up-to-date isSeeking state instead of stale-scrubbing the progress bar.
        this.isSeeking = isSeekingNow
        this.setWasPaused( paused )

        if ( isSeekingNow ) {
            try {
                await this.videoPlayerRef.pause()
            } catch ( err ) { }
        } else {
            try {
                if ( isTryingToSkipAhead && isForwardRestricted ) {
                    this.videoPlayerRef.currentTime = this.maxTimeWatched
                } else {
                    this.videoPlayerRef.currentTime = targetTime
                    this.handleMouseOnTimeline( event, isSeekingNow )
                }
                if ( !paused ) {
                    await this.videoPlayerRef.play()
                } else {
                    await this.videoPlayerRef.pause()
                }
            } catch ( err ) { }
        }

        return { isSeeking: isSeekingNow, wasPaused: paused }
    }

    endSeeking = async ( event ) => {
        document.getElementById( 'controls-container' )?.classList.toggle( 'scrubbing', false )
        if ( !this.isSeeking ) return { isSeeking: false }
        return this.toggleSeeking( event )
    }

    seekPlusTen = () => {
        if ( !this.videoPlayerRef ) return
        const video = this.videoPlayerRef
        const newTime = video.currentTime + 10
        video.currentTime = newTime >= video.duration ? Math.max( 0, video.duration - 2 ) : newTime
    }

    seekMinusTen = () => {
        if ( !this.videoPlayerRef ) return
        const video = this.videoPlayerRef
        const newTime = video.currentTime - 10
        video.currentTime = newTime <= 0 ? 0 : newTime
    }

    toggleFullScreen = async () => {
        const container = this.fullscreenElement || this.videoContainerRef || window.document
        if ( !document.fullscreenElement ) {
            if ( container.requestFullscreen ) container.requestFullscreen()
            else if ( container.webkitRequestFullscreen ) container.webkitRequestFullscreen()
            else if ( container.msRequestFullscreen ) container.msRequestFullscreen()
            try {
                await window.screen.orientation.lock( 'portrait-primary' )
            } catch ( err ) { }
            this.isInFullscreenMode = true
            container.classList.add( 'fullscreen' )
        } else {
            if ( document.exitFullscreen ) document.exitFullscreen()
            else if ( document.webkitExitFullscreen ) document.webkitExitFullscreen()
            else if ( document.msExitFullscreen ) document.msExitFullscreen()
            this.isInFullscreenMode = false
            container.classList.remove( 'fullscreen' )
        }
    }

    handleFullScreenChange = () => {
        if ( !this.videoContainerRef ) return

        if ( document.fullscreenElement ) {
            this.isInFullscreenMode = true
            this.videoContainerRef.classList.add( 'fullscreen' )
        } else {
            this.isInFullscreenMode = false
            this.videoContainerRef.classList.remove( 'fullscreen' )
        }
    }

    handleKeyDown = ( event ) => {
        const { code } = event
        switch ( code ) {
            case 'ArrowRight':
                if ( !this.config.controllerOptions.seeking || !this.config.controllerOptions.forward ) return
                this.seekPlusTen()
                break
            case 'ArrowLeft':
                if ( !this.config.controllerOptions.seeking || !this.config.controllerOptions.backward ) return
                this.seekMinusTen()
                break
            case 'ArrowUp':
                event.preventDefault()
                if ( this.videoPlayerRef && this.videoPlayerRef.volume + 0.1 <= 1 ) {
                    this.videoPlayerRef.volume = this.videoPlayerRef.volume + 0.1
                    this.handleVolumeChange()
                }
                break
            case 'ArrowDown': {
                if ( !this.videoPlayerRef ) break
                event.preventDefault()
                const nextVolume = Number( ( this.videoPlayerRef.volume - 0.1 ).toFixed( 1 ) )
                if ( nextVolume >= 0 ) {
                    this.videoPlayerRef.volume = nextVolume
                    this.videoPlayerRef.muted = nextVolume === 0
                    this.handleVolumeChange()
                }
                break
            }
            case 'KeyF':
                this.toggleFullScreen()
                break
            case 'Space':
                event.preventDefault()
                this.togglePlay()
                break
            default:
                break
        }
    }

    loadDefaults = () => {
        if ( !this.videoPlayerRef ) return
        const video = this.videoPlayerRef
        video.volume = this.config.defaults.volume ?? 1
        video.muted = this.config.defaults.volume === 0
        if ( this.config.defaults.start ) {
            video.currentTime = this.config.defaults.start
        } else if ( typeof this.config.defaults.start_percent === 'number' ) {
            let startTime = ( this.config.defaults.start_percent / 100 ) * video.duration
            if ( this.config.defaults.start_percent === 100 ) {
                startTime -= 10
                startTime = startTime < 0 ? 0 : startTime
            }
            video.currentTime = startTime
        }
    }

    handleAddEventClick = () => {
        if ( this.config.editOptions.canAddTimelineEvents ) {
            this.handleAddEvent()
        }
    }

    handleEventMarkerPointerDown ( event, eventId ) {
        if ( !this.isEditModeActive || !this.timelineContainerRef || !this.config.editOptions.canScrubTimelineEvents ) return
        event.preventDefault()
        event.stopPropagation()
        this.setDraggingEventId( eventId )

    }

    handleEventDrag = ( event ) => {
        if ( !this.draggingEventId || !this.timelineContainerRef || !this.videoPlayerRef ) return

        const boundary = this.timelineContainerRef.getBoundingClientRect()
        const clientX = event.clientX !== undefined
            ? event.clientX
            : ( event.touches?.[0]?.clientX || event.changedTouches?.[0]?.clientX || 0 )
        const offsetX = Math.min( Math.max( 0, clientX - boundary.left ), boundary.width )
        const percent = boundary.width > 0 ? offsetX / boundary.width : 0
        const time = percent * this.duration
        this.setDraggingTime( time )
        this.videoPlayerRef.currentTime = time
        this.timelineContainerRef.style.setProperty( '--progress-position', percent )
        document.getElementById( 'controls-container' )?.classList.add( 'scrubbing' )
        const updated = this.editableEvents.map( ( item ) => ( item.id === this.draggingEventId ? { ...item, time } : item ) )
        this.updateEventList( updated )
    }

    handleEventDragStop = () => {
        const draggedId = this.draggingEventId
        this.setDraggingEventId( null )
        this.setDraggingTime( null )
        document.getElementById( 'controls-container' )?.classList.remove( 'scrubbing' )
        if ( draggedId ) {
            const element = document.getElementById( draggedId )
            if ( element ) {
                element.focus( { preventScroll: true } )
                element.scrollIntoView( { inline: 'center', behavior: 'smooth' } )
            }
        }
    }

    updateProgress = () => {
        const video = this.videoPlayerRef

        if ( !video || !this.timelineContainerRef ) return
        const currentTime = video.currentTime

        this.currentTime = currentTime

        if ( currentTime > this.maxTimeWatched ) {
            this.maxTimeWatched = currentTime
        }
        this.handleVideoData()
        // Timeline events are fired by the metadata track cues (see syncEventCues)
        this.lastTime = currentTime
    }
}

export { defaultControls }

// React context, provider and hook for VideoPlayerStore
const VideoPlayerStoreContext = React.createContext( null )

export const VideoPlayerStoreProvider = ( { children, store } ) => {
    return (
        <VideoPlayerStoreContext.Provider value={store}>{children}</VideoPlayerStoreContext.Provider>
    )
}

export const useVideoPlayerStore = () => {
    const store = React.useContext( VideoPlayerStoreContext )
    if ( !store ) throw new Error( 'useVideoPlayerStore must be used within a VideoPlayerStoreProvider' )
    return store
}